import { useState } from "react";

import { MORE_THAN_FOUR_CATS_COUNT } from "../constants";
import { CatCount, InputError, IUseCatNamesNewFormReturn } from "../types";
import { isRadioValid, validate } from "../utils";

export function useCatNamesNewForm(
  initialNames: string[] = [],
): IUseCatNamesNewFormReturn {
  // Determine initial cat count based on names in store (we don't store count in store)
  const getInitialCatCount = (): CatCount | null => {
    if (initialNames.length === 0) {
      return null;
    }
    if (initialNames.length >= 4) {
      return MORE_THAN_FOUR_CATS_COUNT;
    }
    return initialNames.length as CatCount;
  };

  const [ selectedCatCount, setSelectedCatCount ] = useState<CatCount | null>(getInitialCatCount());
  const [ catNames, setCatNames ] = useState<string[]>(initialNames);
  const [ errors, setErrors ] = useState<Record<number, InputError>>({});
  const [ touchedIndexes, setTouchedIndexes ] = useState<ReadonlySet<number>>(new Set());
  const [ showMoreThanFourCatsCard, setShowMoreThanFourCatsCard ] = useState(false);

  const errorIndexes = new Set(
    Object.entries(errors)
      .filter(([ index ]) => touchedIndexes.has(Number(index)))
      .map(([ index ]) => Number(index)),
  );

  const handleCatCountChange = (count: CatCount) => {
    setSelectedCatCount(count);

    const newNames = Array.from({ length: count }, (_, i) => catNames[i] ?? "");
    setCatNames(newNames);
    setErrors({});
    setTouchedIndexes(new Set());
    setShowMoreThanFourCatsCard(false);
  };

  const updateName = (index: number, value: string) => {
    const newNames = [ ...catNames ];
    newNames[index] = value;
    setCatNames(newNames);

    const updatedTouched = new Set(touchedIndexes);
    updatedTouched.add(index);
    setTouchedIndexes(updatedTouched);

    setErrors(validate(newNames, updatedTouched));
  };

  const validateAll = (): boolean => {
    if (!isRadioValid(selectedCatCount)) {
      return false;
    }

    const allIndexes = new Set(catNames.map((_, i) => i));
    setTouchedIndexes(allIndexes);

    const newErrors = validate(catNames, allIndexes);
    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const getFormattedCatEntries = () => catNames.map((name, index) => ({
    id: `${index + 1}`,
    cat: { name },
  }));

  const toggleMoreThanFourCatsCard = () => {
    setShowMoreThanFourCatsCard((prev) => !prev);
  };

  return {
    selectedCatCount,
    catNames,
    errors,
    errorIndexes,
    showMoreThanFourCatsCard,
    setSelectedCatCount: handleCatCountChange,
    updateName,
    validateAll,
    getFormattedCatEntries,
    toggleMoreThanFourCatsCard,
  };
}
